import { BootFlags, getBootFlag } from '@/app-config';
import { chronosphereLogsClient } from '@/logs';
import { randomString } from '@/utils';

// Use this to enable RUM locally for debugging. Don't forget to disable it when you're done!
const ENABLE_FOR_DEV = false;

const debug = (message?: unknown, ...optionalParams: unknown[]) => {
  if (ENABLE_FOR_DEV) {
    console.debug(message, ...optionalParams);
  }
};

/**
 * Function that sends the RUM event to Chronosphere Logs.
 */
const submitEventToChronosphereLogs = (message: string, level: 'info' | 'error', event: Record<string, unknown>) => {
  const eventPayload = {
    source: 'chronosphere-rum',
    message: `[RUM] ${message}`,
    level,
    happened_at: new Date().toISOString(),
    ...event,
  };

  debug('Sending to logs...', eventPayload);
  chronosphereLogsClient.submitLog(eventPayload);
};

/**
 * Detects device type based on user agent
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const ua = window.navigator.userAgent;

  // iPad detection (iOS 13+ reports as Mac, but has touch support)
  if (/iPad|Macintosh/.test(ua) && 'ontouchend' in document) {
    return 'tablet';
  }

  // Android tablets (have Android but not Mobile in UA)
  if (/Android/.test(ua) && !/Mobile/.test(ua)) {
    return 'tablet';
  }

  // Mobile devices
  if (/Mobi|Android/i.test(ua)) {
    return 'mobile';
  }

  return 'desktop';
};

/**
 * Extracts browser name and version from user agent
 */
const getBrowserInfo = (): { name: string; version: string } => {
  const ua = window.navigator.userAgent;
  const uaLower = ua.toLowerCase();

  // Check for internal/testing tools first
  if (uaLower.includes('ui-prober')) {
    return { name: 'Prober', version: 'unknown' };
  }

  // Edge (Chromium-based)
  if (uaLower.includes('edg/')) {
    const match = ua.match(/Edg\/([\d.]+)/);
    return { name: 'Edge', version: match?.[1] ?? 'unknown' };
  }

  // Opera
  if (uaLower.includes('opr/') || uaLower.includes('opera')) {
    const match = ua.match(/(?:OPR|Opera)\/([\d.]+)/);
    return { name: 'Opera', version: match?.[1] ?? 'unknown' };
  }

  // Chrome (must come before Safari)
  if (uaLower.includes('chrome')) {
    const match = ua.match(/Chrome\/([\d.]+)/);
    return { name: 'Chrome', version: match?.[1] ?? 'unknown' };
  }

  // Safari
  if (uaLower.includes('safari') && !uaLower.includes('chrome')) {
    const match = ua.match(/Version\/([\d.]+)/);
    return { name: 'Safari', version: match?.[1] ?? 'unknown' };
  }

  // Firefox
  if (uaLower.includes('firefox')) {
    const match = ua.match(/Firefox\/([\d.]+)/);
    return { name: 'Firefox', version: match?.[1] ?? 'unknown' };
  }

  // Internet Explorer
  if (uaLower.includes('trident') || uaLower.includes('msie')) {
    const match = ua.match(/(?:MSIE |rv:)([\d.]+)/);
    return { name: 'IE', version: match?.[1] ?? 'unknown' };
  }

  return { name: 'unknown', version: 'unknown' };
};

/**
 * Extracts OS name and version from user agent
 */
const getOSInfo = (): { name: string; version: string } => {
  const ua = window.navigator.userAgent;

  // Windows
  if (/Windows NT/.test(ua)) {
    const versionMap: Record<string, string> = {
      '10.0': '10/11', // Windows 10 and 11 both report 10.0
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    const match = ua.match(/Windows NT ([\d.]+)/);
    const ntVersion = match?.[1];
    const version = ntVersion ? (versionMap[ntVersion] ?? ntVersion) : 'unknown';
    return { name: 'Windows', version };
  }

  // macOS
  if (/Mac OS X/.test(ua)) {
    const match = ua.match(/Mac OS X ([\d_]+)/);
    let version = match?.[1]?.replace(/_/g, '.') ?? 'unknown';

    // Firefox and Safari freeze at 10.15 for privacy on macOS 11+
    // Normalize to indicate it's a frozen version
    if (version === '10.15') {
      version = '10.15+';
    }

    return { name: 'macOS', version };
  }

  // iOS
  if (/iPhone|iPad|iPod/.test(ua)) {
    const match = ua.match(/OS ([\d_]+)/);
    const version = match?.[1]?.replace(/_/g, '.') ?? 'unknown';
    return { name: 'iOS', version };
  }

  // Android
  if (/Android/.test(ua)) {
    const match = ua.match(/Android ([\d.]+)/);
    const version = match?.[1] ?? 'unknown';
    return { name: 'Android', version };
  }

  // Linux
  if (/Linux/.test(ua)) {
    return { name: 'Linux', version: 'unknown' };
  }

  // Chrome OS
  if (/CrOS/.test(ua)) {
    return { name: 'Chrome OS', version: 'unknown' };
  }

  return { name: 'unknown', version: 'unknown' };
};

/**
 * Extracts device model for mobile devices
 */
const getDeviceModel = (): string | undefined => {
  const ua = window.navigator.userAgent;

  // iPhone models
  if (/iPhone/.test(ua)) {
    // iOS doesn't expose specific model in UA, but we can infer from screen size
    const { width, height } = window.screen;
    const deviceMap: Record<string, string> = {
      '390x844': 'iPhone 12/13/14',
      '393x852': 'iPhone 14 Pro',
      '428x926': 'iPhone 12/13/14 Pro Max',
      '430x932': 'iPhone 14 Pro Max',
      '375x812': 'iPhone X/XS/11 Pro',
      '414x896': 'iPhone XR/11/11 Pro Max',
      '375x667': 'iPhone 6/7/8',
      '414x736': 'iPhone 6/7/8 Plus',
      '320x568': 'iPhone SE (1st gen)',
    };
    return deviceMap[`${width}x${height}`] ?? 'iPhone';
  }

  // iPad models
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document)) {
    return 'iPad';
  }

  // Android devices - some include model in UA
  if (/Android/.test(ua)) {
    const match = ua.match(/Android.*;\s([^)]+)\sBuild/);
    const model = match?.[1];
    if (model) {
      // Clean up common manufacturer prefixes
      return model.replace(/^(SM-|SAMSUNG-|LG-|HTC-)/, '');
    }
    return 'Android Device';
  }

  return undefined;
};

// TypeScript types for Web Vitals APIs
interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

/**
 * Map of standard screen resolutions with friendly names
 * Export this for UI components to resolve resolution names
 */
export const SCREEN_RESOLUTION_STANDARDS = [
  { w: 1920, h: 1080, name: 'Full HD' },
  { w: 2560, h: 1440, name: '2K' },
  { w: 3840, h: 2160, name: '4K' },
  { w: 1366, h: 768, name: 'HD' },
  { w: 1440, h: 900, name: 'MacBook' },
  { w: 2880, h: 1800, name: 'MacBook Pro Retina' },
  { w: 1536, h: 864, name: 'HD+' },
  { w: 1280, h: 720, name: 'HD' },
  { w: 1600, h: 900, name: 'HD+' },
  { w: 2560, h: 1600, name: 'MacBook Pro 13"' },
  { w: 3024, h: 1964, name: 'MacBook Pro 14"' },
  { w: 3456, h: 2234, name: 'MacBook Pro 16"' },
] as const;

/**
 * Normalizes screen resolution to common standards for better aggregation
 * Uses ±10px tolerance to group similar resolutions
 * Returns format: "1920x1080" (use SCREEN_RESOLUTION_STANDARDS to resolve names in UI)
 */
const normalizeResolution = (width: number, height: number): string => {
  // Match against standard resolutions with ±10px tolerance
  for (const std of SCREEN_RESOLUTION_STANDARDS) {
    if (Math.abs(width - std.w) <= 10 && Math.abs(height - std.h) <= 10) {
      return `${std.w}x${std.h}`;
    }
  }

  // Round to nearest 10 for non-standard resolutions
  const roundedW = Math.round(width / 10) * 10;
  const roundedH = Math.round(height / 10) * 10;
  return `${roundedW}x${roundedH}`;
};

/**
 * Viewport breakpoint definitions (Tailwind-style)
 * Export this for UI components to resolve breakpoint names
 */
export const VIEWPORT_BREAKPOINTS = [
  { name: 'xs', minWidth: 0, maxWidth: 639, label: 'Mobile' },
  { name: 'sm', minWidth: 640, maxWidth: 767, label: 'Large Mobile' },
  { name: 'md', minWidth: 768, maxWidth: 1023, label: 'Tablet' },
  { name: 'lg', minWidth: 1024, maxWidth: 1279, label: 'Small Desktop' },
  { name: 'xl', minWidth: 1280, maxWidth: 1535, label: 'Desktop' },
  { name: '2xl', minWidth: 1536, maxWidth: Infinity, label: 'Large Desktop' },
] as const;

/**
 * Categorizes viewport width into responsive breakpoints
 * Aligned with common CSS framework breakpoints (Tailwind-style)
 * Returns format: "xs (380x670)" (use VIEWPORT_BREAKPOINTS to resolve labels in UI)
 */
const getViewportBreakpoint = (width: number, height: number): string => {
  const breakpoint = (() => {
    if (width < 640) return 'xs';
    if (width < 768) return 'sm';
    if (width < 1024) return 'md';
    if (width < 1280) return 'lg';
    if (width < 1536) return 'xl';
    return '2xl';
  })();

  // Round dimensions to nearest 10 for grouping
  const roundedW = Math.round(width / 10) * 10;
  const roundedH = Math.round(height / 10) * 10;

  return `${breakpoint} (${roundedW}x${roundedH})`;
};

// Generate unique session ID for correlating events (lazy initialization)
let sessionId: string | undefined;
const getSessionId = () => {
  if (!sessionId) {
    sessionId = randomString();
  }
  return sessionId;
};

// Helper to get common metadata for all events
const getCommonMetadata = () => {
  const browserInfo = getBrowserInfo();
  const osInfo = getOSInfo();
  const deviceModel = getDeviceModel();

  return {
    'rum.sessionId': getSessionId(),
    'rum.timestamp': Date.now(),
    'rum.url': window.location.href,
    'rum.pathName': window.location.pathname,
    'rum.hostName': window.location.hostname,
    'rum.pageName': window.document.title.replace('- Chronosphere', ''),
    'rum.browserName': browserInfo.name,
    'rum.browserVersion': browserInfo.version,
    'rum.os': osInfo.name,
    'rum.osVersion': osInfo.version,
    'rum.deviceType': getDeviceType(),
    ...(deviceModel && { 'rum.deviceModel': deviceModel }), // Only include if detected
    'rum.screenResolution': normalizeResolution(window.screen.width, window.screen.height),
    'rum.viewport': getViewportBreakpoint(window.innerWidth, window.innerHeight),
    'rum.timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'rum.language': window.navigator.language,
    'rum.schemaVersion': 1,
  };
};

// 1. Navigation Timing API - Page load performance
const observeNavigationTiming = () => {
  window.addEventListener('load', () => {
    // Use setTimeout to ensure navigation timing data is available
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (perfData) {
        submitEventToChronosphereLogs('Navigation timing captured', 'info', {
          ...getCommonMetadata(),
          'rum.metricType': 'navigation',
          'rum.dnsLookup': perfData.domainLookupEnd - perfData.domainLookupStart,
          'rum.tcpConnection': perfData.connectEnd - perfData.connectStart,
          'rum.tlsNegotiation':
            perfData.secureConnectionStart > 0 ? perfData.connectEnd - perfData.secureConnectionStart : 0,
          'rum.ttfb': perfData.responseStart - perfData.requestStart,
          'rum.responseTime': perfData.responseEnd - perfData.responseStart,
          'rum.domInteractive': perfData.domInteractive,
          'rum.domContentLoaded': perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
          'rum.domComplete': perfData.domComplete,
          'rum.loadComplete': perfData.loadEventEnd - perfData.loadEventStart,
          'rum.totalLoadTime': perfData.loadEventEnd - perfData.fetchStart,
        });
      }
    }, 0);
  });
};

// 2. Resource Timing - Track individual resource loads
// NOTE: HTTP status codes (responseStatus) have limited browser support:
// - Chrome 109+, Edge 109+: Full support ✅
// - Safari 16.4+: Partial support
// - Firefox: Not yet supported ❌
const observeResourceTiming = () => {
  const captureResources = () => {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const recentResources = resources.slice(-50); // Increased limit since we're batching

    // Separate network requests from static assets
    const networkRequests: PerformanceResourceTiming[] = [];
    const staticAssets: Map<string, PerformanceResourceTiming[]> = new Map();

    recentResources.forEach((resource) => {
      const type = resource.initiatorType;

      // Network requests get individual events for better API performance tracking
      if (type === 'fetch' || type === 'xmlhttprequest') {
        networkRequests.push(resource);
      } else {
        // Batch static assets by type (script, link, img, font, etc.)
        if (!staticAssets.has(type)) {
          staticAssets.set(type, []);
        }
        staticAssets.get(type)!.push(resource);
      }
    });

    // Submit each network request as individual event
    networkRequests.forEach((resource) => {
      // Extract path from URL for easier aggregation
      let requestPath = resource.name;
      try {
        const urlObj = new URL(resource.name);
        requestPath = urlObj.pathname + urlObj.search; // Include query params
      } catch {
        // If URL parsing fails, use the full name
        requestPath = resource.name;
      }

      // responseStatus is only available in Chrome 109+, Safari 16.4+
      // Cast to access the property that may not be in TypeScript types yet
      const responseStatus = (resource as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;

      submitEventToChronosphereLogs('Network request', 'info', {
        ...getCommonMetadata(),
        'rum.metricType': 'networkRequest',
        'rum.requestUrl': resource.name,
        'rum.requestPath': requestPath,
        'rum.requestType': resource.initiatorType,
        'rum.duration': resource.duration,
        'rum.transferSize': resource.transferSize,
        'rum.decodedBodySize': resource.decodedBodySize,
        'rum.startTime': resource.startTime,
        'rum.cached': resource.transferSize === 0 && resource.decodedBodySize > 0,
        ...(responseStatus !== undefined && { 'rum.statusCode': responseStatus }),
        // Timing breakdowns
        'rum.dnsTime': resource.domainLookupEnd - resource.domainLookupStart,
        'rum.tcpTime': resource.connectEnd - resource.connectStart,
        'rum.requestTime': resource.responseStart - resource.requestStart,
        'rum.responseTime': resource.responseEnd - resource.responseStart,
      });
    });

    // Submit batched events for static assets by type
    staticAssets.forEach((assetList, type) => {
      const items = assetList.map((resource) => {
        // responseStatus is only available in Chrome 109+, Safari 16.4+
        const responseStatus = (resource as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;

        return {
          name: resource.name,
          duration: resource.duration,
          transferSize: resource.transferSize,
          decodedBodySize: resource.decodedBodySize,
          startTime: resource.startTime,
          cached: resource.transferSize === 0 && resource.decodedBodySize > 0,
          ...(responseStatus !== undefined && { statusCode: responseStatus }),
        };
      });

      // Calculate aggregate stats for top-level querying
      const totalSize = items.reduce((sum, item) => sum + item.transferSize, 0);
      const avgDuration = items.reduce((sum, item) => sum + item.duration, 0) / items.length;
      const maxDuration = Math.max(...items.map((item) => item.duration));
      const cachedCount = items.filter((item) => item.cached).length;

      submitEventToChronosphereLogs(`Static assets loaded: ${type}`, 'info', {
        ...getCommonMetadata(),
        'rum.metricType': 'staticAssets',
        'rum.assetType': type,
        'rum.count': items.length,
        'rum.totalSize': totalSize,
        'rum.avgDuration': avgDuration,
        'rum.maxDuration': maxDuration,
        'rum.cachedCount': cachedCount,
        'rum.cacheRate': cachedCount / items.length,
        'rum.items': items, // Detailed breakdown still available
      });
    });

    // Clear buffer to prevent memory leaks
    performance.clearResourceTimings();
  };

  // Capture resources periodically (every 30 seconds)
  setInterval(captureResources, 30_000);
};

// 3. Core Web Vitals - Modern performance metrics
// NOTE: Core Web Vitals (LCP, FID, CLS) have limited browser support:
// - Chrome/Edge: Native support ✅
// - Safari 16.4+: Native LCP support, limited FID/CLS
// - Firefox: No native support ❌
// For full cross-browser support, consider using Google's web-vitals library:
// https://github.com/GoogleChrome/web-vitals
// This would provide polyfills for browsers without native support.
const observeWebVitals = () => {
  if (!('PerformanceObserver' in window)) {
    debug('PerformanceObserver not supported, skipping web vitals');
    return;
  }

  try {
    // Largest Contentful Paint (LCP)
    // Store the latest LCP value and element - observer fires multiple times as larger elements render
    let lcpValue: number | undefined;
    let lcpElement: string | undefined;
    let lcpReported = false;

    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number;
        loadTime?: number;
        element?: Element;
      };

      // Update LCP value - this will be the latest (largest) contentful paint
      lcpValue = lastEntry.renderTime || lastEntry.loadTime;
      lcpElement = lastEntry.element?.tagName;
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

    // Report LCP on visibility change or page unload (same as CLS)
    const reportLCP = () => {
      // Prevent duplicate reporting (visibilitychange and pagehide both fire on page close)
      if (lcpReported || lcpValue === undefined) return;
      lcpReported = true;

      submitEventToChronosphereLogs('LCP metric captured', 'info', {
        ...getCommonMetadata(),
        'rum.metricType': 'lcp',
        'rum.value': lcpValue,
        'rum.element': lcpElement,
      });
    };

    // Only report LCP when page becomes hidden (not when it becomes visible again)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportLCP();
      }
    });
    window.addEventListener('pagehide', reportLCP);

    // First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEventTiming;
        submitEventToChronosphereLogs('FID metric captured', 'info', {
          ...getCommonMetadata(),
          'rum.metricType': 'fid',
          'rum.value': fidEntry.processingStart - fidEntry.startTime,
          'rum.eventType': entry.name,
        });
      });
    });
    fidObserver.observe({ type: 'first-input', buffered: true });

    // Cumulative Layout Shift (CLS)
    let clsScore = 0;
    let clsReported = false;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const layoutShift = entry as LayoutShift;
        if (!layoutShift.hadRecentInput) {
          clsScore += layoutShift.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report CLS on visibility change or page unload
    const reportCLS = () => {
      // Prevent duplicate reporting (visibilitychange and pagehide both fire on page close)
      if (clsReported) return;
      clsReported = true;

      submitEventToChronosphereLogs('CLS metric captured', 'info', {
        ...getCommonMetadata(),
        'rum.metricType': 'cls',
        'rum.value': clsScore,
      });
    };

    // Only report CLS when page becomes hidden (not when it becomes visible again)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportCLS();
      }
    });
    window.addEventListener('pagehide', reportCLS);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Web Vitals observation failed:', e);
  }
};

// 4. JavaScript Errors
const observeErrors = () => {
  // Runtime JavaScript errors
  window.addEventListener('error', (event) => {
    if (event.message.match(/ResizeObserver/)) {
      return;
    }

    submitEventToChronosphereLogs('JavaScript error', 'error', {
      ...getCommonMetadata(),
      'rum.metricType': 'error',
      'rum.message': event.message,
      'rum.filename': event.filename,
      'rum.lineno': event.lineno,
      'rum.colno': event.colno,
      'rum.stack': event.error?.stack,
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    submitEventToChronosphereLogs('Unhandled promise rejection', 'error', {
      ...getCommonMetadata(),
      'rum.metricType': 'unhandledRejection',
      'rum.reason': event.reason?.message || String(event.reason),
      'rum.stack': event.reason?.stack,
    });
  });
};

// 5. Long Tasks - Detect main thread blocking
const observeLongTasks = () => {
  if (!('PerformanceObserver' in window)) {
    debug('PerformanceObserver not supported, skipping long tasks');
    return;
  }

  try {
    const longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        submitEventToChronosphereLogs('Long task detected', 'info', {
          ...getCommonMetadata(),
          'rum.metricType': 'longTask',
          'rum.duration': entry.duration,
          'rum.startTime': entry.startTime,
        });
      }
    });
    longTaskObserver.observe({ type: 'longtask', buffered: true });
  } catch (e) {
    debug('Long task observation not supported:', e);
  }
};

// TODO: Features to implement later:
// - User interaction tracking (clicks, form submissions) with privacy considerations
// - Page visibility tracking for user engagement metrics
// - Custom performance marks API (window.RUM.mark/measure) for app-specific timing
// - Network request sampling/rate limiting to reduce log volume
// - sendBeacon API for more reliable event delivery on page unload
// NOTE: Batching is already handled by ChronosphereLogsClient (smartQueue with 3s interval)

/**
 * Monitors network events, timing, and related metrics, publishing to Logs.
 *
 * Note: This function is called at app startup, before user authentication.
 * Events captured during initial page load are buffered by chronosphereLogsClient
 * and sent once identify() is called after authentication completes.
 */
export const initializeRum = () => {
  try {
    const isEnabled = ENABLE_FOR_DEV || getBootFlag(BootFlags.NETWORK_MONITORING_ENABLED) === 'true';
    if (!isEnabled) return;

    debug('Initializing rum...');
    observeNavigationTiming();
    observeResourceTiming();
    observeWebVitals();
    observeErrors();
    observeLongTasks();
  } catch {
    // If anything fails, don't break the UI, just fail silently
    debug('Unable to initialize RUM!');
  }
};
